import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper per convertire il path dell'immagine
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  // Converte /uploads/filename.jpg in /api/images/filename.jpg
  const filename = imageUrl.replace("/uploads/", "");
  return `${API}/images/${filename}`;
};

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart } = useCart();
  const { customer, isLoggedIn } = useCustomer();
  const [cartCalculation, setCartCalculation] = useState(null);
  const [loading, setLoading] = useState(false);

  const isBusiness = isLoggedIn && customer?.account_type === "business";

  useEffect(() => {
    if (cart.length > 0) {
      calculateCart();
    } else {
      setCartCalculation(null);
    }
  }, [cart]);

  const calculateCart = async () => {
    setLoading(true);
    try {
      const items = cart.map((item) => ({
        id: item.id,
        product_id: item.id,
        quantity: item.quantity,
        name: item.name,
        price: item.price,
        business_only: item.business_only || false,
      }));

      const response = await axios.post(`${API}/cart/calculate`, { items });
      setCartCalculation(response.data);
    } catch (error) {
      console.error("Errore nel calcolo del carrello:", error);
      toast.error("Errore nel calcolo del carrello");
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (productId, newQuantity, maxStock) => {
    if (newQuantity < 1) return;
    
    if (newQuantity > maxStock) {
      toast.error(`Disponibilità insufficiente! Solo ${maxStock} pezzi in magazzino.`);
      return;
    }
    
    updateQuantity(productId, newQuantity);
  };

  const handleRemove = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`${productName} rimosso dal carrello`);
  };

  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23111' width='200' height='150'/%3E%3Ctext fill='%23666' font-family='sans-serif' font-size='12' text-anchor='middle' x='100' y='75'%3EPREGIATO%3C/text%3E%3C/svg%3E";

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-4" data-testid="empty-cart">
          <div className="text-center">
            <ShoppingBag className="w-24 h-24 text-zinc-700 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Il tuo carrello è vuoto</h2>
            <p className="text-zinc-400 mb-8">Aggiungi prodotti dal catalogo per iniziare</p>
            <button
              onClick={() => navigate("/catalogo")}
              className="bg-green-500 text-black px-8 py-3 rounded-lg font-bold hover:bg-green-600 transition-all"
              data-testid="go-to-catalog-button"
            >
              Vai al catalogo
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full" data-testid="cart-page">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8" data-testid="cart-title">Carrello</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.map((item) => {
              const imageUrl = item.image_url
                ? getImageUrl(item.image_url)
                : placeholderImage;

              return (
                <div
                  key={item.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 flex gap-4"
                  data-testid={`cart-item-${item.id}`}
                >
                  <img
                    src={imageUrl}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded"
                    data-testid="cart-item-image"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1" data-testid="cart-item-name">{item.name}</h3>
                    <p className="text-sm text-zinc-400 mb-1" data-testid="cart-item-price">€{item.price.toFixed(2)}</p>
                    <p className="text-xs text-zinc-500 mb-3" data-testid="cart-item-stock">
                      {item.stock || 0} disponibili in magazzino
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 bg-black rounded-lg border border-zinc-700">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1, item.stock || 0)}
                          className="p-2 hover:bg-zinc-800 rounded-l-lg transition-colors"
                          data-testid="decrease-quantity"
                        >
                          <Minus className="w-4 h-4 text-white" />
                        </button>
                        <span className="px-4 text-white font-semibold" data-testid="item-quantity">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1, item.stock || 0)}
                          disabled={item.quantity >= (item.stock || 0)}
                          className={`p-2 rounded-r-lg transition-colors ${
                            item.quantity >= (item.stock || 0) 
                              ? "bg-zinc-800 cursor-not-allowed" 
                              : "hover:bg-zinc-800"
                          }`}
                          data-testid="increase-quantity"
                        >
                          <Plus className={`w-4 h-4 ${item.quantity >= (item.stock || 0) ? "text-zinc-600" : "text-white"}`} />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemove(item.id, item.name)}
                        className="p-2 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all"
                        data-testid="remove-item"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-white" data-testid="item-total">
                      €{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 sticky top-24" data-testid="cart-summary">
              <h2 className="text-2xl font-bold text-white mb-6">Riepilogo</h2>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : cartCalculation ? (
                <>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-zinc-300">
                      <span>Subtotale</span>
                      <span data-testid="subtotal">€{cartCalculation.subtotal.toFixed(2)}</span>
                    </div>
                    {cartCalculation.discount_percentage > 0 && (
                      <div className="flex justify-between text-green-500 font-semibold">
                        <span>Sconto ({cartCalculation.discount_percentage}%)</span>
                        <span data-testid="discount">-€{cartCalculation.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-zinc-300">
                      <span>Spedizione</span>
                      <span data-testid="shipping" className={cartCalculation.shipping_cost === 0 ? "text-green-500" : ""}>
                        {cartCalculation.shipping_cost === 0 ? "Gratuita" : `€${cartCalculation.shipping_cost.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="border-t border-zinc-700 pt-3 flex justify-between text-white text-xl font-bold">
                      <span>Totale</span>
                      <span data-testid="total">€{cartCalculation.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {!cartCalculation.can_checkout && (
                    <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-4" data-testid="minimum-order-warning">
                      <p className="text-yellow-500 text-sm font-semibold">
                        Ti mancano €{cartCalculation.amount_to_minimum.toFixed(2)} per raggiungere l'ordine minimo (30€)
                      </p>
                    </div>
                  )}

                  {cartCalculation.can_checkout && cartCalculation.shipping_cost > 0 && !(isLoggedIn && customer?.partner_company_id) && (
                    <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4 mb-4" data-testid="free-shipping-info">
                      <p className="text-blue-400 text-sm">
                        Aggiungi €{cartCalculation.amount_to_free_shipping.toFixed(2)} per la spedizione gratuita!
                      </p>
                    </div>
                  )}

                  {cartCalculation.can_checkout && cartCalculation.amount_to_next_discount !== null && !isBusiness && (
                    <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-4" data-testid="next-discount-info">
                      <p className="text-green-500 text-sm">
                        Ti mancano €{cartCalculation.amount_to_next_discount.toFixed(2)} per raggiungere lo sconto successivo!
                      </p>
                    </div>
                  )}

                  <button
                    onClick={() => navigate("/checkout")}
                    disabled={!cartCalculation.can_checkout}
                    className={`w-full py-4 rounded-lg font-bold text-lg transition-all ${
                      cartCalculation.can_checkout
                        ? "bg-yellow-500 text-black hover:bg-yellow-600"
                        : "bg-zinc-800 text-zinc-600 cursor-not-allowed"
                    }`}
                    data-testid="checkout-button"
                  >
                    Procedi al checkout
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
